import { type FC, type KeyboardEvent } from 'react';
import { ExternalLink, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type { HotelPartner } from '@/types';

export interface HotelCardProps {
  hotel: HotelPartner;
  onSelect: (hotel: HotelPartner) => void;
}

export const HotelCard: FC<HotelCardProps> = ({ hotel, onSelect }) => {
  const imageSrc = hotel.imageUrl ?? hotel.backgroundImageUrl;

  const handleSelect = (): void => {
    onSelect(hotel);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={hotel.name}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      className="cursor-pointer overflow-hidden hover:shadow-xl transition-shadow focus:outline-none focus:ring-2 focus:ring-orange-500"
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={hotel.name}
          className="w-full h-48 object-cover"
        />
      ) : (
        <div
          aria-hidden="true"
          className="w-full h-48 bg-gradient-to-br from-turquoise-700 to-turquoise-900 flex items-center justify-center"
        >
          <span className="font-viking text-2xl text-white text-center px-4">
            {hotel.name}
          </span>
        </div>
      )}

      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-viking text-xl text-white">{hotel.name}</h3>
          {hotel.isPrimary && <Badge variant="default">Host Hotel</Badge>}
        </div>

        <div className="flex items-start gap-2 text-sm text-white">
          <MapPin className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <div>
            <div>{hotel.address}</div>
            <div>
              {hotel.city}, {hotel.state} {hotel.zipCode}
            </div>
          </div>
        </div>

        {hotel.distanceFromVenue && (
          <div className="text-sm text-white">
            <span className="inline-block bg-turquoise-50 text-turquoise-800 border border-turquoise-200 rounded-full px-2.5 py-0.5 text-xs">
              {hotel.distanceFromVenue} from venue
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
          <span className="text-sm text-orange-600 font-medium flex-1">
            View details
          </span>
          {hotel.bookingUrl && (
            <a
              href={hotel.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center justify-center rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-3 py-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Book Your Room
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
